import { z } from 'zod'
import { feetToPixels, pixelsToFeet } from '../constants/scale.js'
import {
  feetToHundredths,
  formatFeet,
  hundredthsToFeet,
  hundredthsToFeetOrNull,
  isHundredthsFeet,
  parseFeetInput,
} from './feet-hundredths.js'
import type { Primitive } from '../dto/primitive.dto.js'
import type { FurnitureClass } from '../enums/furniture-class.enum.js'

/**
 * The largest side a dance floor may be given, in feet.
 *
 * A plan offers every size from the default up to this, so the cap is what
 * keeps that list finite. A hundred feet is past any room this app draws.
 */
export const DANCE_FLOOR_MAX_FEET = 100

/**
 * How many sizes a dance floor may offer.
 *
 * The list is a `<select>`, the same control text size uses. A tenth-of-a-foot
 * step up to the maximum would be thousands of rows, which is no longer a
 * choice someone can make. Two hundred is the most that control can hold and
 * still be a control.
 */
const DANCE_FLOOR_MAX_SIZES = 200

/** A size field: positive, at most two decimal places, and within the cap. */
export const danceFloorFeetSchema = z
  .number()
  .positive('Enter a size greater than 0.')
  .max(DANCE_FLOOR_MAX_FEET, `Size cannot exceed ${String(DANCE_FLOOR_MAX_FEET)} ft.`)
  .refine(isHundredthsFeet, 'Use at most two decimal places.')

/**
 * Why `defaultFeet` and `incrementFeet` are not a dance floor, or null when
 * they are.
 *
 * Sizes are `default + n * increment` for an integer n of 0 or more, and both
 * sides of the square are that one number. 10 with a step of 1 is 10, 11, 12;
 * it is never 9, 10.5, or a 10 by 11.
 */
export function danceFloorSpecError(defaultFeet: number | null, incrementFeet: number | null): string | null {
  if (defaultFeet === null || incrementFeet === null) {
    return 'A dance floor needs a default size and a size increment.'
  }
  if (!isHundredthsFeet(defaultFeet) || !isHundredthsFeet(incrementFeet)) {
    return 'Use at most two decimal places.'
  }
  if (defaultFeet <= 0 || incrementFeet <= 0) {
    return 'Default size and size increment must be greater than 0.'
  }
  if (defaultFeet > DANCE_FLOOR_MAX_FEET || incrementFeet > DANCE_FLOOR_MAX_FEET) {
    return `Default size and size increment cannot exceed ${String(DANCE_FLOOR_MAX_FEET)} ft.`
  }
  const start = feetToHundredths(defaultFeet)
  const step = feetToHundredths(incrementFeet)
  const max = feetToHundredths(DANCE_FLOOR_MAX_FEET)
  const count = Math.floor((max - start) / step) + 1
  if (count > DANCE_FLOOR_MAX_SIZES) {
    return 'That increment makes more sizes than a dance floor can offer. Use a larger increment.'
  }
  return null
}

/**
 * What the furniture forms show, and what they send.
 *
 * Blank is not an error yet: both boxes have to hold a number before the spec
 * can be wrong, and an empty box is reported by the save. Anything that is
 * not a dance floor sends neither number and shows nothing.
 */
export function danceFloorFormValues(
  furnitureClass: FurnitureClass,
  defaultText: string,
  incrementText: string,
): { defaultFeet: number | null; incrementFeet: number | null; error: string | null } {
  if (furnitureClass !== 'DanceFloor') {
    return { defaultFeet: null, incrementFeet: null, error: null }
  }
  const defaultFeet = parseFeetInput(defaultText)
  const incrementFeet = parseFeetInput(incrementText)
  return {
    defaultFeet,
    incrementFeet,
    error: defaultFeet !== null && incrementFeet !== null ? danceFloorSpecError(defaultFeet, incrementFeet) : null,
  }
}

/** The two numbers a dance floor stores, or neither. Anything else stores neither. */
export interface DanceFloorSpec {
  defaultFeet: number | null
  incrementFeet: number | null
}

/**
 * The spec to store for a piece that will be `furnitureClass`.
 *
 * Anything that is not a dance floor stores neither number. A dance floor
 * stores both, or the write is refused — a dance floor without them has no
 * size to place and no list to offer.
 */
export function settleDanceFloor(
  furnitureClass: FurnitureClass,
  defaultFeet: number | null,
  incrementFeet: number | null,
): { ok: true; spec: DanceFloorSpec } | { ok: false; message: string } {
  if (furnitureClass !== 'DanceFloor') {
    return { ok: true, spec: { defaultFeet: null, incrementFeet: null } }
  }
  const message = danceFloorSpecError(defaultFeet, incrementFeet)
  if (message !== null || defaultFeet === null || incrementFeet === null) {
    return { ok: false, message: message ?? 'A dance floor needs a default size and a size increment.' }
  }
  return {
    ok: true,
    spec: {
      defaultFeet: hundredthsToFeet(feetToHundredths(defaultFeet)),
      incrementFeet: hundredthsToFeet(feetToHundredths(incrementFeet)),
    },
  }
}

/**
 * What an update schema can refuse without the stored row.
 *
 * A pair of numbers is settled as a dance floor even when the class is
 * omitted, so a pair that could never be offered is refused before the
 * service sees it. Clearing a number while the class is DanceFloor is the
 * same refusal. One number on its own is left alone: only the service knows
 * the stored other half, and pairing them here would be a guess.
 */
export function danceFloorUpdateIssue(
  furnitureClass: FurnitureClass | undefined,
  defaultFeet: number | null | undefined,
  incrementFeet: number | null | undefined,
): string | null {
  if (furnitureClass === 'DanceFloor' && (defaultFeet === null || incrementFeet === null)) {
    const cleared = settleDanceFloor('DanceFloor', null, null)
    return cleared.ok ? null : cleared.message
  }
  if (typeof defaultFeet === 'number' && typeof incrementFeet === 'number') {
    const pair = settleDanceFloor('DanceFloor', defaultFeet, incrementFeet)
    return pair.ok ? null : pair.message
  }
  return null
}

/** The side lengths a dance floor may be placed at, in feet, smallest first. */
export function danceFloorSizes(defaultFeet: number, incrementFeet: number): number[] {
  if (danceFloorSpecError(defaultFeet, incrementFeet) !== null) return []
  const start = feetToHundredths(defaultFeet)
  const step = feetToHundredths(incrementFeet)
  const max = feetToHundredths(DANCE_FLOOR_MAX_FEET)
  const sizes: number[] = []
  for (let hundredths = start; hundredths <= max; hundredths += step) {
    sizes.push(hundredthsToFeet(hundredths))
  }
  return sizes
}

/** One row of the plan's size control. `hundredths` is the select value. */
export interface DanceFloorSizeOption {
  hundredths: number
  label: string
  /**
   * False when this is the size already on the plan and the catalog no longer
   * offers it. It stays selected so opening the plan does not resize the floor.
   */
  offered: boolean
}

/** "10 × 10 ft". Both sides, because a 10 by 11 is not a size this offers. */
export function formatDanceFloorSize(feet: number): string {
  const n = formatFeet(feet)
  return `${n} × ${n} ft`
}

/**
 * The sizes the plan may offer, plus the size already placed when that is no
 * longer one of them.
 *
 * Same shape as text sizes: the current value has to be an option or the
 * select shows blank and the next change silently picks the first row.
 */
export function danceFloorSizeOptions(
  defaultFeet: number,
  incrementFeet: number,
  currentFeet: number | null,
): DanceFloorSizeOption[] {
  const sizes = danceFloorSizes(defaultFeet, incrementFeet)
  const options: DanceFloorSizeOption[] = sizes.map((feet) => ({
    hundredths: feetToHundredths(feet),
    label: formatDanceFloorSize(feet),
    offered: true,
  }))
  if (currentFeet === null || !Number.isFinite(currentFeet) || currentFeet <= 0) return options
  const current = feetToHundredths(currentFeet)
  if (options.some((option) => option.hundredths === current)) return options
  options.push({
    hundredths: current,
    label: formatDanceFloorSize(hundredthsToFeet(current)),
    offered: false,
  })
  options.sort((a, b) => a.hundredths - b.hundredths)
  return options
}

/**
 * The side of a placed dance floor, in feet, or null when it is not one square.
 *
 * Null is what the plan shows as "choose a size": a drawing that is not a
 * square has no size to keep, and the next choice replaces it with one.
 */
export function danceFloorSideFeet(primitives: readonly Primitive[]): number | null {
  const only = primitives.length === 1 ? primitives[0] : undefined
  if (only === undefined || only.shape !== 'rect') return null
  const width = feetToHundredths(pixelsToFeet(only.width))
  const height = feetToHundredths(pixelsToFeet(only.height))
  if (width !== height || width <= 0) return null
  return hundredthsToFeet(width)
}

/**
 * The one square a dance floor is.
 *
 * Centered on the piece's anchor, which is how every other primitive is
 * placed. Paint and the primitive's id are kept, so resizing on a plan does
 * not clear a fill or look like a different shape to anything that keyed on
 * the id. A drawing the client sent is not: a dance floor is this square.
 */
export function danceFloorPrimitives(feet: number, existing: readonly Primitive[] = []): Primitive[] {
  const rect = existing.find((primitive) => primitive.shape === 'rect')
  const side = feetToPixels(hundredthsToFeet(feetToHundredths(feet)))
  const square: Primitive = {
    id: rect?.id ?? 'dance-floor',
    shape: 'rect',
    x: 0,
    y: 0,
    rotation: 0,
    width: side,
    height: side,
    ...(rect?.shape === 'rect' && rect.fill !== undefined ? { fill: rect.fill } : {}),
    ...(rect?.shape === 'rect' && rect.stroke !== undefined ? { stroke: rect.stroke } : {}),
    ...(rect?.shape === 'rect' && rect.strokeWidth !== undefined ? { strokeWidth: rect.strokeWidth } : {}),
    ...(rect?.shape === 'rect' && rect.cornerRadius !== undefined ? { cornerRadius: rect.cornerRadius } : {}),
  }
  return [square]
}

/** The columns to write, in hundredths of a foot, and the square when it changes. */
export interface DanceFloorWrite {
  defaultHundredths: number | null
  incrementHundredths: number | null
  primitives?: Primitive[]
}

/** The row an update is changing. Hundredths, because that is how the row stores them. */
export interface DanceFloorStored {
  existingClass: FurnitureClass
  existingDefaultHundredths: number | null
  existingIncrementHundredths: number | null
  existingPrimitives: readonly Primitive[]
}

/**
 * The fields an update sent. A missing field is left as stored. Null is an
 * explicit clear. `primitives` being present means the client sent a drawing;
 * the drawing itself is not what gets stored.
 */
export interface DanceFloorPatch {
  class?: FurnitureClass
  defaultFeet?: number | null
  incrementFeet?: number | null
  primitives?: readonly Primitive[]
}

function columnsFrom(spec: DanceFloorSpec, existingPrimitives: readonly Primitive[], regenerate: boolean): DanceFloorWrite {
  const write: DanceFloorWrite = {
    defaultHundredths: spec.defaultFeet === null ? null : feetToHundredths(spec.defaultFeet),
    incrementHundredths: spec.incrementFeet === null ? null : feetToHundredths(spec.incrementFeet),
  }
  if (regenerate && spec.defaultFeet !== null) {
    write.primitives = danceFloorPrimitives(spec.defaultFeet, existingPrimitives)
  }
  return write
}

/**
 * The columns a create stores. A dance floor's square replaces whatever
 * drawing arrived with it. Anything else stores neither number and keeps the
 * drawing the caller already has.
 */
export function danceFloorCreate(
  furnitureClass: FurnitureClass,
  defaultFeet: number | null,
  incrementFeet: number | null,
): { ok: true; write: DanceFloorWrite } | { ok: false; message: string } {
  const settled = settleDanceFloor(furnitureClass, defaultFeet, incrementFeet)
  if (!settled.ok) return settled
  return { ok: true, write: columnsFrom(settled.spec, [], furnitureClass === 'DanceFloor') }
}

/**
 * The columns an update stores, or null when this patch does not mention a
 * dance floor and the columns should be left alone.
 *
 * A class, either size, or a drawing of a piece that will be a dance floor
 * all count. A drawing of anything else does not: reshaping a chair is not a
 * dance floor being cleared. The square is rewritten when the class changes,
 * the default changes, or a drawing arrives for a dance floor. A new
 * increment does not rewrite it — the square is the default, and the
 * increment only says how that square may grow once it is on a plan.
 */
export function danceFloorUpdate(
  stored: DanceFloorStored,
  patch: DanceFloorPatch,
): { ok: true; write: DanceFloorWrite | null } | { ok: false; message: string } {
  const nextClass = patch.class ?? stored.existingClass
  const mentionsDanceFloor =
    patch.class !== undefined ||
    patch.defaultFeet !== undefined ||
    patch.incrementFeet !== undefined ||
    (patch.primitives !== undefined && nextClass === 'DanceFloor')
  if (!mentionsDanceFloor) return { ok: true, write: null }

  const defaultFeet =
    patch.defaultFeet !== undefined ? patch.defaultFeet : hundredthsToFeetOrNull(stored.existingDefaultHundredths)
  const incrementFeet =
    patch.incrementFeet !== undefined
      ? patch.incrementFeet
      : hundredthsToFeetOrNull(stored.existingIncrementHundredths)
  const settled = settleDanceFloor(nextClass, defaultFeet, incrementFeet)
  if (!settled.ok) return settled
  const classChanged = patch.class !== undefined && patch.class !== stored.existingClass
  const regenerate =
    nextClass === 'DanceFloor' && (classChanged || patch.defaultFeet !== undefined || patch.primitives !== undefined)
  return { ok: true, write: columnsFrom(settled.spec, stored.existingPrimitives, regenerate) }
}
